import React, { useEffect, useRef } from 'react';

function WhatsAppChatView({ phone, messages, loading, onClose }) {
  const bottomRef = useRef(null);

  useEffect(() => {
    if (bottomRef.current && !loading) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return 'Hoy';
    if (d.toDateString() === yesterday.toDateString()) return 'Ayer';
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const sorted = [...(messages || [])].sort(
    (a, b) => new Date(a.processed_at) - new Date(b.processed_at)
  );

  const grouped = [];
  let lastDate = '';
  sorted.forEach((msg) => {
    const dateStr = formatDate(msg.processed_at);
    if (dateStr !== lastDate) {
      grouped.push({ type: 'date', label: dateStr });
      lastDate = dateStr;
    }
    grouped.push({ type: 'message', data: msg });
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div className="flex flex-col w-full max-w-2xl shadow-2xl overflow-hidden"
        style={{ maxHeight: '85vh', borderRadius: '16px', backgroundColor: '#f0f0f0' }}
        onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>

        {/* Header - teal accent */}
        <div style={{ backgroundColor: '#4BA3A3', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="20" height="20" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ color: 'white', fontWeight: '700', fontSize: '16px' }}>{phone}</div>
            <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '12px' }}>
              {sorted.length} mensaje{sorted.length !== 1 ? 's' : ''}
            </div>
          </div>
          <button onClick={onClose} title="Cerrar"
            style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white', fontSize: '20px', lineHeight: '1' }}>
            &times;
          </button>
        </div>

        {/* Chat body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', backgroundColor: '#e5ddd5' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <div style={{ width: '32px', height: '32px', border: '3px solid #4BA3A3', borderTopColor: 'transparent', borderRadius: '50%', animation: 'wa-spin 0.8s linear infinite', margin: '0 auto' }} />
              <style>{'@keyframes wa-spin { to { transform: rotate(360deg); } }'}</style>
            </div>
          ) : grouped.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: '#999', fontSize: '14px' }}>Sin mensajes</div>
          ) : (
            grouped.map((item, idx) => {
              if (item.type === 'date') {
                return (
                  <div key={'date-' + idx} style={{ textAlign: 'center', margin: '16px 0 8px' }}>
                    <span style={{ display: 'inline-block', backgroundColor: 'rgba(255,255,255,0.85)', color: '#666', fontSize: '12px', fontWeight: '600', padding: '4px 14px', borderRadius: '8px', boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }}>
                      {item.label}
                    </span>
                  </div>
                );
              }
              const msg = item.data;
              return (
                <div key={'msg-' + msg.id + '-' + idx}>
                  {/* Client message - left side, white bubble */}
                  {msg.message_content && (
                    <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '4px' }}>
                      <div style={{ maxWidth: '75%', backgroundColor: '#ffffff', borderRadius: '0 12px 12px 12px', padding: '8px 12px', boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }}>
                        {msg.detected_language && msg.detected_language !== 'es' && (
                          <span style={{ display: 'inline-block', fontSize: '10px', fontWeight: '600', color: '#7c3aed', backgroundColor: '#ede9fe', padding: '1px 6px', borderRadius: '4px', marginBottom: '4px' }}>
                            {msg.detected_language.toUpperCase()}
                          </span>
                        )}
                        <div style={{ fontSize: '14px', color: '#1a1a1a', lineHeight: '1.5', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {msg.message_content}
                        </div>
                        <div style={{ fontSize: '11px', color: '#999', textAlign: 'right', marginTop: '4px' }}>
                          {formatTime(msg.processed_at)}
                        </div>
                      </div>
                    </div>
                  )}
                  {/* XIA response - right side, green bubble */}
                  {msg.xia_response && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                      <div style={{ maxWidth: '75%', backgroundColor: '#dcf8c6', borderRadius: '12px 0 12px 12px', padding: '8px 12px', boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }}>
                        <div style={{ fontSize: '14px', color: '#1a1a1a', lineHeight: '1.5', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {msg.xia_response}
                        </div>
                        <div style={{ fontSize: '11px', color: '#6b9c5a', textAlign: 'right', marginTop: '4px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                          {formatTime(msg.processed_at)}
                          <svg width="16" height="11" viewBox="0 0 16 11" fill="none">
                            <path d="M11 1L4.5 8.5L1.5 5.5" stroke="#6b9c5a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M14.5 1L8 8.5L6.5 7" stroke="#6b9c5a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>
      </div>
    </div>
  );
}

export default WhatsAppChatView;
